import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Label, Icon } from 'semantic-ui-react';
import { DatePicker } from '../shared/DatePicker';
import WeekDaysSelect from '../shared/WeekDaysSelect';
import PropTypes from 'prop-types';
import { onSuccess, onError, onWarning } from '../shared/messages';
import RatesDropDown from '../shared/RatesDropDown';
import RoomTypesDropDown from '../shared/RoomTypesDropDown';
import ReservationPoliciesDropDown from '../shared/ReservationPoliciesDropDown';
import { ReservationPolicy } from '../../api/services/next';
import _ from 'lodash';
import { useTextResources } from '../../language';
import moment from 'moment';
import { isMobileOnly, isBrowser } from 'mobile-device-detect';
import { useLoader } from '../../hooks/useLoader';
// import ValidateModalPermission from '../shared/ValidateModalPermission';
// import { PermissionsActions } from '../shared/permissionsActions';
import { RateCategory } from '../shared/constants';
import { toServerDateFormat } from '../shared/utils';

const ReservationPolicyUpdatePopup = ({
  onClose,
  onSave,
  HotelID,
  controller,
  startOpen,
  ...rest
}) => {
  const text = useTextResources();
  const loader = useLoader();
  const [dates, setDates] = useState([new Date(), new Date()]);
  const [open, setOpen] = useState(startOpen);
  const [days, setDays] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hotelLevel, setHotelLevel] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [rate, setRate] = useState(null);
  const [policyId, setPolicyId] = useState([]);
  const [params, setParams] = useState(null);
  const [hotelId, setHotelId] = useState(HotelID);
  const [selectedDates, setSelectedDates] = useState([]);

  useEffect(() => {
    if (controller) {
      window[controller] = (action, params) => {
        switch (action) {
          case 'open':
            if (params) {
              setDates([
                params.StartDate || new Date(),
                params.EndDate || new Date(),
              ]);
              setRate(params.RateID);
              setRoomTypes(params.RoomTypeIds || []);
              setHotelId(params.HotelId || HotelID);
              setParams(params);
            } else {
              setDates([new Date(), new Date()]);
              setRate(null);
              setRoomTypes([]);
              setPolicyId(null);
              setParams(null);
            }
            setHotelLevel(false);
            setOpen(true);
            setPolicyId(null);
            setSelectedDates([]);
            break;
          default:
            break;
        }
      };
    }
  }, []);

  useEffect(() => {
    if (hotelLevel) {
      setRate(null);
      setRoomTypes([]);
    }
  }, [hotelLevel]);

  const saveAction = async (close) => {
    setIsSaving(true);
    loader(true);
    try {
      const allDates = selectedDates.length
        ? selectedDates
        : [
            {
              startDate: dates[0],
              endDate: dates[1],
            },
          ];
      const exceptionsToSend = allDates.map((date) => ({
        ReservationPolicyId: policyId,
        StartDate: date.startDate ? toServerDateFormat(date.startDate) : null,
        EndDate: date.endDate ? toServerDateFormat(date.endDate) : null,
        WeekDays: days,
        RoomTypeIds: roomTypes,
        RateId: rate,
        HotelId: hotelId,
        ForceHotelRestriction: hotelLevel,
      }));
      const updated =
        await ReservationPolicy.AddReservationPolicyExceptions(
          exceptionsToSend
        );

      if (onSave) {
        onSave(updated);
      }
      if (params && params.OnSave) {
        params.OnSave(updated);
      }
      onSuccess();
      if (close) {
        setOpen(false);
        if (onClose) onClose();
      }
    } catch (error) {
      console.error(error);
      error.status === 400 ? onWarning(error) : onError(error);
    }
    loader(false);
    setIsSaving(false);
  };

  const closeAction = () => {
    setOpen(false);
    if (onClose) onClose();
  };
  const RoomTypesSpec = React.useMemo(() => {
    let spec = {
      hotelId,
    };
    if (rate) {
      spec.RateIDs = rate;
    }
    return spec;
  }, [hotelId, rate]);

  const RatesSpec = React.useMemo(() => {
    let spec = {
      hotelId,
      IncludeDayUse: false,
      IncludeRatePricingType: true,
      IsActive: true,
      InheritedRates: true,
        FromDate: toServerDateFormat(_.min(dates)),
        ToDate: toServerDateFormat(_.max(dates)),
      ExcludeRateCategoryIDs: [
        RateCategory.ExtraAddOn,
        RateCategory.DerivedPromotion,
      ],
    };

    if (!_.isEmpty(roomTypes)) {
      spec.RoomTypeIds = roomTypes;
    }
    return spec;
  }, [hotelId, roomTypes, dates[0], dates[1]]);

  const isSaveDisabled = () => {
    return ( !policyId ||
      isSaving || !(hotelLevel || !_.isEmpty(roomTypes) || rate )
    );
  };

  const addDateToList = () => {
    if (
      selectedDates.some(
        (d) =>
          moment(d.startDate).isSame(dates[0], 'day') &&
          moment(d.endDate).isSame(dates[1], 'day')
      )
    ) {
      onWarning(text('WarningDuplicateDates'));
      return;
    }
    setSelectedDates((selectedDates) =>
      selectedDates.concat({
        startDate: dates[0],
        endDate: dates[1],
      })
    );
  };

  const handleDatesChanged = (newValue) => {
    if (newValue) {
      setDates(newValue);
    } else {
      setDates([new Date(), new Date()]);
    }
  };

  const onRemoveDateFromSelectedDates = (index, e) => {
    if (e) {
      setSelectedDates((selectedDates) =>
        selectedDates.filter((_, i) => i !== index)
      );
    }
  };

  const resetFields = async () => {
    await setSelectedDates([]);
    await setDates([new Date(), new Date()]);
  };

  const memoDates = useMemo(() => {
    return selectedDates.length
      ? selectedDates
      : [{ startDate: dates[0], endDate: dates[1] }];
  }, [dates, selectedDates]);

  return (
    //TODO - check if special permissions are needed
    // <ValidateModalPermission
    //   open={open}
    //   onClose={onClose}
    //   actionId={PermissionsActions.AccessRestrictionsPopup}
    // >
    <Modal
      size={isMobileOnly ? 'fullscreen' : 'tiny'}
      className={isMobileOnly ? 'mobile' : ''}
      open={open}
      style={{ zIndex: 1000000 }}
      {...rest}
    >
      {!isMobileOnly && (
        <Modal.Header>{text('ReservationPolicyUpdate')}</Modal.Header>
      )}
      <Modal.Content
        style={isMobileOnly ? { overflowY: 'auto' } : { maxHeight: 600 }}
      >
        <Form size='small'>
          <Form.Group unstackable widths={'equal'} style={{ flexWrap: 'wrap' }}>
            {selectedDates.map((selectedDate, index) => (
              <Label.Group key={index}>
                <Label as='a'>
                  {moment(selectedDate.startDate).format('DD/MM/YYYY')}-
                  {moment(selectedDate.endDate).format('DD/MM/YYYY')}
                  <Icon
                    key={index}
                    name={'delete'}
                    onClick={onRemoveDateFromSelectedDates.bind(this, index)}
                  />
                </Label>
              </Label.Group>
            ))}
          </Form.Group>
          <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
            <Form.Field width={12} style={{ display: 'grid' }}>
              <DatePicker
                picktype='range'
                onChange={handleDatesChanged}
                label='Date Range'
                value={dates}
                minDate={moment().startOf('day').toDate()}
              />
            </Form.Field>
            <Form.Field widths={4}>
              <Button size='small' primary onClick={addDateToList}>
                {text('AddDate')}
              </Button>
            </Form.Field>
          </Form.Group>
          <Form.Field
            label={text('Weekdays')}
            control={WeekDaysSelect}
            onChange={setDays}
            dates={memoDates}
          />
          <Form.Checkbox
            label={text('HotelLevel')}
            onChange={(e, { checked }) => setHotelLevel(checked)}
            checked={hotelLevel}
          />
          {!hotelLevel && (
            <React.Fragment>
              <Form.Group widths={2} unstackable>
                <Form.Field
                  label={text('RoomType')}
                  clearable
                  multiple
                  placeholder={text("All")}
                  search={isBrowser}
                  control={RoomTypesDropDown}
                  spec={RoomTypesSpec}
                  onChange={setRoomTypes}
                  value={roomTypes}
                />
                <Form.Field
                  label={text('Rate')}
                  clearable
                  search={isBrowser}
                  control={RatesDropDown}
                  spec={RatesSpec}
                  onChange={setRate}
                  value={rate}
                />
              </Form.Group>
            </React.Fragment>
          )}
          <Form.Field
            label={text('Policy')}
            clearable
            search={isBrowser}
            control={ReservationPoliciesDropDown}
            descriptionField='Name'
            spec={{
              GetOnlyParentPolicies: true
            }}
            onChange={setPolicyId}
            value={policyId}
            required={true}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        {isBrowser && (
          <Button
            size={isMobileOnly ? 'tiny' : 'small'}
            disabled={isSaving}
            negative
            onClick={closeAction}
          >
            {text('Close')}
          </Button>
        )}
        {isBrowser && (
          <Button size={'small'} onClick={resetFields}>
            {text('Reset')}
          </Button>
        )}
        {isBrowser && (
          <Button
            size={isMobileOnly ? 'tiny' : 'small'}
            disabled={isSaveDisabled()}
            loading={isSaving}
            positive
            onClick={() => saveAction(true)}
          >
            {text('Saveandclose')}
          </Button>
        )}
        <Button
          size={isMobileOnly ? 'tiny' : 'small'}
          disabled={isSaveDisabled()}
          loading={isSaving}
          positive
          onClick={() => saveAction(false)}
        >
          {text('Save')}
        </Button>
      </Modal.Actions>
    </Modal>
    // </ValidateModalPermission>
  );
};

ReservationPolicyUpdatePopup.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  HotelID: PropTypes.any,
  controller: PropTypes.string,
  startOpen: PropTypes.bool,
};

export default ReservationPolicyUpdatePopup;
